/**
 * 积分、奖品与兑换接口。路径见方案 4.5b。
 *
 * | 方法 | 路径 |
 * | --- | --- |
 * | GET | `/students/:id/points` |
 * | GET | `/students/:id/points/ledger?from&to&reason` |
 * | POST | `/students/:id/points/adjust`（家长手动奖励/扣分，**理由必填**） |
 * | POST | `/points/ledger/:id/reverse`（纠错冲正，**不修改原记录**） |
 * | GET | `/students/:id/rewards` · POST/PATCH/DELETE `/rewards` |
 * | POST | `/students/:id/redemptions`（事务内校验余额 + 锁行，支持 Idempotency-Key） |
 * | GET | `/students/:id/redemptions` |
 * | POST | `/redemptions/:id/approve \| reject \| fulfill \| cancel` |
 *
 * 容错：未就绪的接口降级为 `{ entries: [], notReady: true }` 之类的空结果，
 * 页面显示"该功能尚未就绪"，不抛未捕获异常。
 */
import type { PointsReason, RedemptionStatus, SharePermission } from '@fht/shared';
import { api, tryRequest } from './client';
import { pickArray, pickObject, toNumber, toOptionalNumber } from './shape';
import type { PointsLedgerEntry, PointsSummary, Redemption, Reward } from './types';

// ---------------------------------------------------------------- 余额

export interface PointsSummaryResult extends PointsSummary {
  /**
   * 本周 / 本月净变化（家庭时区：周一起 / 1 号起，**含冲正**）。
   *
   * ⚠️ 后端字段名是 `weekChange` / `monthChange`（见 `apps/api` 的 `PointsSummary`）。
   * 2026-10-18 的 bug 就出在这里：旧代码只读 `weekDelta` / `monthDelta`，
   * 而后端从来没有这两个键 → 一律解成 `undefined` → 页面兜底成 0，
   * 于是「本周 / 本月变化」永远显示 `0 / 0`（余额 / 累计都对，只有这一格是假的）。
   *
   * `null` = 后端没给这一项（旧接口）：界面显示「—」，**绝不拿 0 冒充**。
   */
  weekChange: number | null;
  monthChange: number | null;
  /** 扣分（`penalty` 净额，正数）—— 单独一栏，不混进"累计消费"。 */
  penaltyTotal: number | null;
  /** 调账 · 冲正（`adjust` 净额，可正可负）—— 同样不进"获得/消费"。 */
  adjustmentTotal: number | null;
  /** 流水合计（正常应等于 balance）。 */
  ledgerSum: number | null;
  /** 账实相符（后端跑过 `verify_points_balance()`）。 */
  balanceConsistent: boolean;
  /** 本周 / 本月的起始日（家庭时区 'YYYY-MM-DD'）。 */
  weekStartDate: string | null;
  monthStartDate: string | null;
  /** 家庭时区（如 `Asia/Shanghai`）。 */
  timezone: string | null;
  /** true = 接口未就绪 */
  notReady: boolean;
}

/**
 * 把 `GET /students/:id/points` 的响应解码成页面模型。
 *
 * 抽成纯函数是为了能用**真实响应**写用例（见 `__tests__/points-summary.test.ts`）：
 * 这类"字段名对不上"的 bug 不会报错、不会白屏，只会让某一格永远显示 0 ——
 * 只有拿真实形状喂进去才钉得住。
 */
export function decodePointsSummary(data: unknown): PointsSummaryResult {
  const record = pickObject(data, {}) as Record<string, unknown>;
  return {
    balance: toNumber(record.balance, 0),
    totalEarned: toOptionalNumber(record.totalEarned) ?? 0,
    totalSpent: toOptionalNumber(record.totalSpent) ?? 0,
    // 后端键是 weekChange / monthChange；weekDelta / monthDelta 只作旧字段兜底。
    weekChange: toOptionalNumber(record.weekChange) ?? toOptionalNumber(record.weekDelta),
    monthChange: toOptionalNumber(record.monthChange) ?? toOptionalNumber(record.monthDelta),
    penaltyTotal: toOptionalNumber(record.penaltyTotal),
    adjustmentTotal: toOptionalNumber(record.adjustmentTotal),
    ledgerSum: toOptionalNumber(record.ledgerSum),
    balanceConsistent: record.balanceConsistent === true,
    weekStartDate: typeof record.weekStartDate === 'string' ? record.weekStartDate : null,
    monthStartDate: typeof record.monthStartDate === 'string' ? record.monthStartDate : null,
    timezone: typeof record.timezone === 'string' ? record.timezone : null,
    notReady: false,
  };
}

export async function getPointsSummary(studentId: string): Promise<PointsSummaryResult> {
  const result = await tryRequest(() => api.get<unknown>(`/students/${studentId}/points`));
  if (result.state === 'notReady') {
    return {
      balance: 0,
      totalEarned: 0,
      totalSpent: 0,
      weekChange: null,
      monthChange: null,
      penaltyTotal: null,
      adjustmentTotal: null,
      ledgerSum: null,
      balanceConsistent: false,
      weekStartDate: null,
      monthStartDate: null,
      timezone: null,
      notReady: true,
    };
  }
  return decodePointsSummary(result.data);
}

// ---------------------------------------------------------------- 流水

export interface LedgerEntry extends PointsLedgerEntry {
  /** 被冲正的原流水 id（纠错链路）。 */
  reversesLedgerId?: string | null;
  /** 这条流水是否已经被冲正过（后端标记；没有时前端按 reversesLedgerId 自行推断）。 */
  reversed?: boolean;
  refCheckinId?: string | null;
  refRedemptionId?: string | null;
  actorType?: string | null;
}

export interface LedgerResult {
  entries: LedgerEntry[];
  total: number;
  page: number;
  pageSize: number;
  notReady: boolean;
}

function normalizeLedgerEntry(raw: unknown): LedgerEntry | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : '';
  if (!id) return null;
  return {
    id,
    delta: toNumber(record.delta, 0),
    balanceAfter: toOptionalNumber(record.balanceAfter) ?? undefined,
    reason: (typeof record.reason === 'string' ? record.reason : 'adjust') as PointsReason,
    note: typeof record.note === 'string' ? record.note : null,
    refId: typeof record.refId === 'string' ? record.refId : null,
    createdAt: typeof record.createdAt === 'string' ? record.createdAt : '',
    reversesLedgerId:
      typeof record.reversesLedgerId === 'string' ? record.reversesLedgerId : null,
    reversed: record.reversed === true,
    refCheckinId: typeof record.refCheckinId === 'string' ? record.refCheckinId : null,
    refRedemptionId: typeof record.refRedemptionId === 'string' ? record.refRedemptionId : null,
    actorType: typeof record.actorType === 'string' ? record.actorType : null,
  };
}

export async function getLedger(
  studentId: string,
  query: { page?: number; pageSize?: number; from?: string; to?: string; reason?: PointsReason } = {},
): Promise<LedgerResult> {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? 20;
  const result = await tryRequest(() =>
    api.get<unknown>(`/students/${studentId}/points/ledger`, { ...query, page, pageSize }),
  );
  if (result.state === 'notReady') {
    return { entries: [], total: 0, page, pageSize, notReady: true };
  }

  const record = pickObject(result.data, {}) as Record<string, unknown>;
  const entries = pickArray<unknown>(record, 'entries', 'items', 'ledger')
    .map(normalizeLedgerEntry)
    .filter((entry): entry is LedgerEntry => entry !== null);

  return {
    entries,
    total: toNumber(record.total, entries.length),
    page: toNumber(record.page, page),
    pageSize: toNumber(record.pageSize, pageSize),
    notReady: false,
  };
}

/** 家长手动奖励 / 扣分。`note`（理由）必填，会进审计（见方案 3.4.1）。 */
export async function adjustPoints(
  studentId: string,
  body: { delta: number; reason: Extract<PointsReason, 'bonus' | 'penalty' | 'adjust'>; note: string },
  idempotencyKey?: string,
): Promise<void> {
  await api.post(
    `/students/${studentId}/points/adjust`,
    body,
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {},
  );
}

/**
 * 纠错冲正：对某笔流水生成**反向流水**，原记录不被修改（见方案 3.4.1）。
 * 注意后端可能要求 body 里带理由，这里一并传上。
 */
export async function reverseLedgerEntry(
  ledgerId: string,
  note: string,
  idempotencyKey?: string,
): Promise<void> {
  await api.post(
    `/points/ledger/${ledgerId}/reverse`,
    { note },
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {},
  );
}

// ---------------------------------------------------------------- 奖品

export interface RewardListResult {
  rewards: Reward[];
  notReady: boolean;
}

/** 家长目录接口附带的孩子（余额 + 我对他的权限）。 */
export interface RewardStudentBalance {
  id: string;
  nickname: string;
  balance: number;
  /** 我对这个孩子的共享权限（owner/editor/viewer）。 */
  permission: SharePermission;
}

export interface MyRewardCatalogResult extends RewardListResult {
  students: RewardStudentBalance[];
}

/** 可见范围：字段缺失时按 `studentId` 推断（字段整个缺失 = 迁移前形状，全是专属）。 */
function readRewardScope(record: Record<string, unknown>, studentId: string | null): 'shared' | 'exclusive' {
  if (record.scope === 'shared' || record.scope === 'exclusive') return record.scope;
  if (record.studentId === undefined) return 'exclusive';
  return studentId === null ? 'shared' : 'exclusive';
}

function normalizeReward(raw: unknown): Reward | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : '';
  if (!id) return null;
  const studentId = typeof record.studentId === 'string' ? record.studentId : null;
  return {
    id,
    name: typeof record.name === 'string' ? record.name : '未命名奖品',
    description: typeof record.description === 'string' ? record.description : '',
    costPoints: toNumber(record.costPoints, 0),
    stock: toOptionalNumber(record.stock),
    needApproval: record.needApproval !== false,
    imageUrl: typeof record.imageUrl === 'string' ? record.imageUrl : null,
    active: record.active !== false,
    studentId,
    scope: readRewardScope(record, studentId),
    studentNickname: typeof record.studentNickname === 'string' ? record.studentNickname : null,
    ...(typeof record.ownerParentId === 'string' ? { ownerParentId: record.ownerParentId } : {}),
  };
}

/**
 * 某学生的奖品目录（学生端只显示 active，并显示"还差 X 分"）。
 * 优先用带 studentId 的路径；后端如果只实现了全局 `/rewards`，会退回去再试一次。
 */
export async function listStudentRewards(studentId: string): Promise<RewardListResult> {
  // 学生视角的路径可能挂在 `/rewards?studentId=` 上（家长维护用的全局路径），
  // 两条都试一次；"404 未就绪"与"功能够用但目录为空"必须区分开。
  const primary = await tryRequest(() => api.get<unknown>(`/students/${studentId}/rewards`));
  if (primary.state === 'ok') {
    return { rewards: normalizeRewards(primary.data), notReady: false };
  }

  const fallback = await tryRequest(() => api.get<unknown>('/rewards', { studentId }));
  if (fallback.state === 'notReady') return { rewards: [], notReady: true };
  return { rewards: normalizeRewards(fallback.data), notReady: false };
}

/**
 * **家长的一整套奖品目录**（`GET /rewards`）。
 *
 * 奖品是家长作用域的：共用奖品在响应里只有一条（`studentId: null`），
 * 不再按孩子重复出现。`students` 给我名下每个孩子的余额，
 * 界面据此回答"哪个孩子换得起"——共用奖品的可兑换性因人而异，
 * 服务端对这类行统一按余额 0 计算（见 rewards.service.ts 的说明）。
 */
export async function listMyRewardCatalog(): Promise<MyRewardCatalogResult> {
  const result = await tryRequest(() => api.get<unknown>('/rewards'));
  if (result.state === 'notReady') return { rewards: [], students: [], notReady: true };

  const record = (result.data ?? {}) as Record<string, unknown>;
  const students = pickArray<unknown>(record, 'students')
    .map((raw): RewardStudentBalance | null => {
      if (raw === null || typeof raw !== 'object') return null;
      const child = raw as Record<string, unknown>;
      const id = typeof child.id === 'string' ? child.id : '';
      if (!id) return null;
      const permission = child.permission;
      return {
        id,
        nickname: typeof child.nickname === 'string' ? child.nickname : '孩子',
        balance: toNumber(child.balance, 0),
        // 认不出来的一律按最保守的 viewer（界面只用来决定"显示不显示按钮"，
        // 真正的门禁在服务端）
        permission:
          permission === 'owner' || permission === 'editor' || permission === 'viewer'
            ? permission
            : 'viewer',
      };
    })
    .filter((child): child is RewardStudentBalance => child !== null);

  return { rewards: normalizeRewards(result.data), students, notReady: false };
}

function normalizeRewards(data: unknown): Reward[] {
  return pickArray<unknown>(data, 'rewards', 'items')
    .map(normalizeReward)
    .filter((reward): reward is Reward => reward !== null);
}

export interface RewardWritePayload {
  name: string;
  description: string;
  costPoints: number;
  stock: number | null;
  needApproval: boolean;
  imageUrl: string | null;
  active: boolean;
  /**
   * **可见范围**：省略 / `null` = 归属家长名下所有孩子共用（默认）；
   * 给某个孩子的 id = 仅他可见。
   */
  studentId?: string | null;
}

export async function createReward(payload: RewardWritePayload): Promise<void> {
  await api.post('/rewards', payload);
}

export async function updateReward(id: string, payload: Partial<RewardWritePayload>): Promise<void> {
  await api.patch(`/rewards/${id}`, payload);
}

export async function deleteReward(id: string): Promise<void> {
  await api.del(`/rewards/${id}`);
}

// ---------------------------------------------------------------- 兑换

function normalizeRedemption(raw: unknown): Redemption | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : '';
  if (!id) return null;
  const status = (
    ['pending', 'approved', 'rejected', 'fulfilled', 'cancelled'] as RedemptionStatus[]
  ).includes(record.status as RedemptionStatus)
    ? (record.status as RedemptionStatus)
    : 'pending';

  return {
    id,
    studentId: typeof record.studentId === 'string' ? record.studentId : undefined,
    rewardId: typeof record.rewardId === 'string' ? record.rewardId : '',
    rewardNameSnapshot:
      (typeof record.rewardNameSnapshot === 'string' && record.rewardNameSnapshot) ||
      (typeof record.rewardName === 'string' && record.rewardName) ||
      '奖品',
    costPoints: toNumber(record.costPoints, 0),
    status,
    note: typeof record.note === 'string' ? record.note : '',
    decisionNote: typeof record.decisionNote === 'string' ? record.decisionNote : '',
    requestedAt: typeof record.requestedAt === 'string' ? record.requestedAt : '',
    decidedAt: typeof record.decidedAt === 'string' ? record.decidedAt : null,
    fulfilledAt: typeof record.fulfilledAt === 'string' ? record.fulfilledAt : null,
  };
}

export interface RedemptionListResult {
  redemptions: Redemption[];
  notReady: boolean;
}

export async function listRedemptions(
  studentId: string,
  status?: RedemptionStatus,
): Promise<RedemptionListResult> {
  const result = await tryRequest(() =>
    api.get<unknown>(`/students/${studentId}/redemptions`, status ? { status } : {}),
  );
  if (result.state === 'notReady') return { redemptions: [], notReady: true };
  // ⚠️ 后端 `listRedemptions` 返回的键是 **`entries`**（见 rewards.service.ts 的返回类型
  //   `{ entries, total, page, pageSize, balance }`）。早先候选只有
  //   `redemptions` / `items`，一个都不匹配 → 兑换审批页**永远显示空列表**，
  //   家长看不到孩子的兑换申请，也就无法审批（等于兑换闭环断了一环）。
  const list = pickArray<unknown>(result.data, 'entries', 'redemptions', 'items')
    .map(normalizeRedemption)
    .filter((item): item is Redemption => item !== null);
  return { redemptions: list, notReady: false };
}

/** 学生发起兑换。`Idempotency-Key` 必带：连点两次不会超兑（见方案 3.4.2）。 */
export async function requestRedemption(
  studentId: string,
  body: { rewardId: string; note: string },
  idempotencyKey: string,
): Promise<void> {
  await api.post(`/students/${studentId}/redemptions`, body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** 家长审批 / 履约 / 驳回 / 取消。 */
export type RedemptionDecision = 'approve' | 'reject' | 'fulfill' | 'cancel';

/**
 * 每个决策对应一个**独立注册**的后端路由（`/redemptions/:id/approve` 等，见方案 4.5b）。
 *
 * 这里刻意写成显式映射而不是模板字符串 `${decision}`：
 *  1. 静态路径能被 `scripts/check-api-paths.mjs` 抽出来比对，模板串抽不出来；
 *  2. 少一层"变量拼出来的路径对不对"的推理，改后端路径时一眼就能看到要改哪几行。
 */
const DECISION_PATHS: Record<RedemptionDecision, (id: string) => string> = {
  approve: (id) => `/redemptions/${id}/approve`,
  reject: (id) => `/redemptions/${id}/reject`,
  fulfill: (id) => `/redemptions/${id}/fulfill`,
  cancel: (id) => `/redemptions/${id}/cancel`,
};

export async function decideRedemption(
  id: string,
  decision: RedemptionDecision,
  note: string,
  idempotencyKey?: string,
): Promise<void> {
  await api.post(
    DECISION_PATHS[decision](id),
    { note },
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {},
  );
}

export { normalizeRedemption };
